import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Eye } from "lucide-react";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Reactable } from "../../components/ui/reactable";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISALS } from "../../constants/api";

import StatusPill, { STATUS_META } from "../../components/appraisal/StatusPill";
import useAppraisalRole, { useAppraisalPermissions } from "../../hooks/useAppraisalRole";

const STATUS_OPTIONS = [
    "pending_goal_agreement",
    "goals_sent_back",
    "goals_agreed",
    "submitted_to_rm",
    "sent_back_by_rm",
    "rm_reviewed",
    "pending_hr_approval",
    "hr_approved",
    "published",
    "overdue",
].map((s) => ({ value: s, label: STATUS_META[s]?.label || s.replace(/_/g, " ") }));

/* Filters live on the columns, so Reactable's funnel holds them all and the
   screen carries no loose controls. Nested values are flattened onto the row
   first, because client-side matching reads row[accessor] directly. */
const buildColumns = (onOpen) => [
    {
        accessor: "employee_name", Header: "Employee", sortable: true, filterable: true,
        Cell: ({ row }) => (
            <div>
                <button
                    className="font-medium hover:text-sidebar-primary text-left"
                    onClick={(e) => { e.stopPropagation(); onOpen(row.id); }}
                >
                    {row.employee_name || "—"}
                </button>
                <p className="text-xs text-muted-foreground">{row.employee_designation}</p>
            </div>
        ),
    },
    {
        accessor: "cycle_name", Header: "Cycle", sortable: true, filterable: true,
        Cell: ({ value }) => value || "—",
    },
    {
        accessor: "self_submitted_at", Header: "Submitted", sortable: true,
        filterable: true, filterType: "date",
        Cell: ({ value }) => (
            <span className="text-xs text-muted-foreground">
                {value ? dayjs(value).format("DD-MM-YYYY") : "—"}
            </span>
        ),
    },
    {
        accessor: "status", Header: "Status", sortable: true, filterable: true,
        filterType: "select", filterOptions: STATUS_OPTIONS,
        Cell: ({ value }) => <StatusPill value={value} />,
    },
    {
        accessor: "action", Header: "Action",
        Cell: ({ row }) => (
            <Button variant="ghost" size="sm"
                onClick={(e) => { e.stopPropagation(); onOpen(row.id); }}>
                <Eye className="w-4 h-4 mr-1" /> Review
            </Button>
        ),
    },
];

const TeamReviewList = () => {
    const navigate = useNavigate();
    const { fetch } = useApiMethods();
    const { isRM } = useAppraisalRole();
    const { canAssignAppraisal } = useAppraisalPermissions();

    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadData = useCallback(async () => {
        if (!isRM) { setLoading(false); return; }
        setLoading(true);
        try {
            const res = await fetch(APPRAISALS, { scope: "team" });
            setTeam(res?.data || []);
        } catch (_) {
            ToastMsg("error", "Failed to load team appraisals");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRM]);

    useEffect(() => { loadData(); }, [loadData]);

    const rows = useMemo(
        () => team.map((a) => ({
            ...a,
            employee_name: a.employee?.name || "",
            employee_designation: a.employee?.designation || "",
        })),
        [team],
    );

    if (!isRM) {
        return (
            <ContainerCard scrollable>
                <ModuleTitleCard Icon={Users} title="Team Review" subtitle="Only available to Reporting Managers" />
                <div className="text-center py-12 text-muted-foreground">
                    You don't have any subordinates configured. Contact HR if you should.
                </div>
            </ContainerCard>
        );
    }

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={Users}
                title="Team Review"
                subtitle="Appraisals from your direct reports"
                totalRecords={rows.length}
                buttonEnable={canAssignAppraisal}
                buttonName="Assign Appraisal"
                setOpenDialog={() => navigate("/appraisal/team-review/assign")}
            />

            {loading ? (
                <div className="text-center py-10 text-muted-foreground">Loading…</div>
            ) : (
                <Reactable
                    pagename="team-review"
                    data={rows}
                    setData={setTeam}
                    columns={buildColumns((id) => navigate(`/appraisal/team-review/${id}`))}
                    isCSVRequired
                    isPDFRequired
                    url={APPRAISALS}
                    printFilter={{ scope: "team" }}
                    rowSize="sm"
                    tableHeight="calc(100vh - 300px)"
                />
            )}
        </ContainerCard>
    );
};

export default TeamReviewList;
